import {TrackingKeyVariants} from '@/components/tracked-atoms/types';
import {DependencyRegistrer, ValueFactory, ModelFactory, ValueResolver, VisibilitySet} from '@/engine';
import {createEvents} from '@/engine/document-model/reducers';
import {createConditionFactory, getConditionDependencies} from '@/engine/processor/condition';
import {createEventHandler} from '@/engine/processor/event';
import {TableRow} from '@/engine/types/document-model/document-table';
import {getValueDependencies} from '@/engine/utils/dependencies';
import {recursiveReplaceIndex} from '@/engine/utils/object';
import {DomainValueReference, LocalizedString, TemplateValue, TableFields, SupportsSwitch} from '@/model/types';
import {createFactoryFromSwitch} from './switch';

export const computeTableHeaders = (
  headers: SupportsSwitch<LocalizedString>[],
  dependencyRegistrer?: DependencyRegistrer,
): ModelFactory<LocalizedString>[] => {
  return headers.map(header => {
    if ('$switch' in header) {
      return createFactoryFromSwitch(header, {fr: '', en: ''}, {dependencyRegistrer});
    }
    return () => header;
  });
};

type TableValueType = DomainValueReference | TemplateValue | LocalizedString | string | undefined;
export const computeTableValue = (
  value: TableFields['table']['columns'][0]['value'],
  dependencyRegistrer?: DependencyRegistrer,
): ModelFactory<TableValueType> => {
  if (!value) return () => '';

  if (typeof value !== 'string' && '$switch' in value) {
    return createFactoryFromSwitch(value, undefined, {dependencyRegistrer});
  }

  dependencyRegistrer?.('all', ...getValueDependencies(value));

  return () => value;
};

export const computeTableRows = (
  table: TableFields['table'],
  dependencyRegistrer: DependencyRegistrer,
): ValueFactory<TableRow[]> => {
  if (!table) return () => [];

  const arrayRowCountFieldId = table.array ? `_${table.array}[count]` : undefined;

  if (arrayRowCountFieldId) {
    dependencyRegistrer?.('visibility', arrayRowCountFieldId);
  }

  if (table.disableRowIf) {
    const deps = table.disableRowIf.flatMap(getConditionDependencies);
    dependencyRegistrer?.('all', ...deps);
  }

  return (store, valueResolver, visibilitySet) => {
    let rowCount: number;
    if (table.staticRowCount) {
      rowCount = table.staticRowCount;
    } else {
      rowCount = Number(valueResolver(store?.getState().data[arrayRowCountFieldId ?? '']));
    }

    const computeEvents = createEvents(store, valueResolver, visibilitySet, undefined);

    const rows = [];

    for (let index = 0; index < rowCount; index++) {
      const row = {columns: [], onClick: []} as TableRow;

      if (table.filters) {
        const shouldDisplayRow = table.filters.every(filter => {
          const condition = recursiveReplaceIndex(filter, index);
          return createConditionFactory(condition, dependencyRegistrer)(valueResolver, visibilitySet)();
        });
        if (!shouldDisplayRow) continue;
      }

      row.disabled = computeRowDisabledIf(table.disableRowIf, index, dependencyRegistrer, valueResolver, visibilitySet);
      row.actionButton = computeRowActionButton(
        table.actionButton,
        index,
        dependencyRegistrer,
        valueResolver,
        visibilitySet,
      );
      row.columns = computeRowColumns(table.columns, index, dependencyRegistrer, valueResolver, visibilitySet);
      row.onClick = computeRowEvents(table.onRowClick, index, dependencyRegistrer, computeEvents);

      rows.push(row);
    }

    return rows;
  };
};

const computeRowDisabledIf = (
  conditions: TableFields['table']['disableRowIf'],
  index: number,
  dependencyRegistrer: DependencyRegistrer,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
): boolean => {
  if (!conditions) return false;

  return conditions.every(condition => {
    const resolvedCondition = recursiveReplaceIndex(condition, index);
    return createConditionFactory(resolvedCondition, dependencyRegistrer)(valueResolver, visibilitySet)();
  });
};

const computeRowActionButton = (
  actionButton: TableFields['table']['actionButton'],
  index: number,
  dependencyRegistrer: DependencyRegistrer,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
): TableRow['actionButton'] => {
  if (!actionButton) return undefined;

  const resolvedButton = recursiveReplaceIndex(actionButton, index);
  const button =
    typeof resolvedButton === 'object' && '$switch' in resolvedButton
      ? createFactoryFromSwitch(
          resolvedButton,
          {label: {fr: '', en: ''}, theme: undefined, trackingKey: ''},
          {dependencyRegistrer},
        )(valueResolver, visibilitySet)
      : resolvedButton;

  return {
    label: valueResolver(button.label),
    theme: button.theme,
    trackingKey: button.trackingKey as TrackingKeyVariants,
  };
};

const computeRowColumns = (
  columns: TableFields['table']['columns'],
  index: number,
  dependencyRegistrer: DependencyRegistrer,
  valueResolver: ValueResolver,
  visibilitySet: VisibilitySet,
): TableRow['columns'] => {
  return columns.map(column => {
    const fieldFromQuery = recursiveReplaceIndex(column.value, index);
    return computeTableValue(fieldFromQuery, dependencyRegistrer)(valueResolver, visibilitySet);
  });
};

const computeRowEvents = (
  onRowClick: TableFields['table']['onRowClick'],
  index: number,
  dependencyRegistrer: DependencyRegistrer,
  computeEvents: ReturnType<typeof createEvents>,
): TableRow['onClick'] => {
  return computeEvents(
    (onRowClick ?? []).map(event => {
      const resolvedEvent = recursiveReplaceIndex(event, index);

      // In initialValues of newEntryAction, {$index} in field names refers to the target array index,
      // not the source table row. Restore original fields so recursiveReplaceIndex doesn't overwrite them.
      if (resolvedEvent.action.type === 'newEntry' && resolvedEvent.action.initialValues) {
        const original = event.action as {initialValues: {field: string; value: unknown}[]};
        resolvedEvent.action.initialValues = resolvedEvent.action.initialValues.map((resolvedEntry, entryIndex) => ({
          ...resolvedEntry,
          field: original.initialValues[entryIndex].field,
        }));
      }

      // In the $lookup selector of editEntryAction, {$index} in field names refers to the target array index,
      // not the source table row. Restore original field so recursiveReplaceIndex doesn't overwrite it.
      if (
        resolvedEvent.action.type === 'editEntry' &&
        event.action.type === 'editEntry' &&
        typeof resolvedEvent.action.row === 'object' &&
        typeof event.action.row === 'object'
      ) {
        resolvedEvent.action.row.$lookup.field = event.action.row.$lookup.field;
      }

      return createEventHandler(resolvedEvent, dependencyRegistrer);
    }),
  );
};
